import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  doc as docRef,
  getDoc,
  deleteDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export default function BatchDetails({ batch, onBack }) {
  const [loading, setLoading]           = useState(true);
  const [teacherName, setTeacherName]   = useState('');
  const [attendance, setAttendance]     = useState([]);
  const [tests, setTests]               = useState([]);
  const [studentsMap, setStudentsMap]   = useState({});
  const [session, setSession]           = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewType, setViewType]         = useState('attendance');
  const [testResults, setTestResults]   = useState([]);
  const [loadingTest, setLoadingTest]   = useState(false);

  // Add-Student form state
  const [showAdd, setShowAdd]           = useState(false);
  const [newStudent, setNewStudent]     = useState({
    name: '', email: '', mobile: '', class: '', school: '',
    father_name: '', father_phone: '', mother_name: '', mother_phone: '',
    date_of_joining: '', total_fees: ''
  });
  const [adding, setAdding]             = useState(false);
  const [addError, setAddError]         = useState('');

  // 1) Load batch details, attendance, tests, student names
  useEffect(() => {
    (async () => {
      // teacher’s name
      try {
        const td = await getDoc(docRef(db, 'users', batch.teacher_id));
        if (td.exists()) setTeacherName(td.data().name || '');
      } catch {}

      // attendance
      const attSnap = await getDocs(
        query(collection(db, 'attendance'), where('batch_id','==',batch.id))
      );
      setAttendance(attSnap.docs.map(d => ({ id:d.id, ...d.data() })));

      // tests
      const testSnap = await getDocs(
        query(collection(db, 'tests'), where('batch_id','==',batch.id))
      );
      setTests(testSnap.docs.map(d => ({ id:d.id, ...d.data() })));

      // student name map
      const stuSnap = await getDocs(
        query(collection(db, 'students'), where('batch_ids','array-contains',batch.id))
      );
      const map = {};
      stuSnap.docs.forEach(d => { map[d.id] = d.data().name; });
      setStudentsMap(map);

      setLoading(false);
    })();
  }, [batch]);

  // 2) Reset attendance session when switching view
  useEffect(() => {
    if (viewType === 'attendance') {
      setSession(null);
      setStatusFilter('all');
    }
  }, [viewType]);

  // 3) When a test is selected, load & rank results
  useEffect(() => {
    if (viewType === 'attendance') return;
    (async () => {
      setLoadingTest(true);
      const snap = await getDocs(
        query(collection(db,'testResults'), where('test_id','==',viewType))
      );
      const raw = snap.docs.map(d => d.data());
      const ids = [...new Set(raw.map(r=>r.test_id))];
      const metas = await Promise.all(ids.map(id=>getDoc(docRef(db,'tests',id))));
      const metaMap = {};
      metas.forEach(m => { if (m.exists()) metaMap[m.id]=m.data().topic; });
      setTestResults(
        raw
          .map(r => ({
            ...r,
            topic: metaMap[r.test_id] || 'Unknown'
          }))
          .sort((a,b) => b.marks - a.marks)
      );
      setLoadingTest(false);
    })();
  }, [viewType]);

  // Add new student
  const handleAddSubmit = async e => {
    e.preventDefault();
    setAdding(true);
    setAddError('');
    try {
      await addDoc(collection(db,'students'), {
        ...newStudent,
        batch_ids:       [batch.id],
        date_of_joining: new Date(newStudent.date_of_joining),
        total_fees:      Number(newStudent.total_fees),
        created_at:      serverTimestamp()
      });
      setShowAdd(false);
      setNewStudent({
        name:'', email:'', mobile:'', class:'', school:'',
        father_name:'', father_phone:'', mother_name:'', mother_phone:'',
        date_of_joining:'', total_fees:''
      });
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  // Delete batch + all its data
  const handleDeleteBatch = async () => {
    // delete attendance
    const attSnap = await getDocs(
      query(collection(db,'attendance'), where('batch_id','==',batch.id))
    );
    await Promise.all(attSnap.docs.map(d=>deleteDoc(docRef(db,'attendance',d.id))));
    // delete tests+results
    const testSnap = await getDocs(
      query(collection(db,'tests'), where('batch_id','==',batch.id))
    );
    await Promise.all(testSnap.docs.map(async tdoc => {
      const resSnap = await getDocs(
        query(collection(db,'testResults'), where('test_id','==',tdoc.id))
      );
      await Promise.all(resSnap.docs.map(d=>deleteDoc(docRef(db,'testResults',d.id))));
      await deleteDoc(docRef(db,'tests',tdoc.id));
    }));
    // delete batch itself
    await deleteDoc(docRef(db,'batches',batch.id));
    onBack();
  };

  if (loading) return <p>Loading batch details…</p>;

  // unique sessions
  const sessions = attendance.reduce((acc,a) => {
    const key = `${a.date}||${a.topic}`;
    if (!acc.find(s=>s.key===key)) acc.push({ key, date:a.date, topic:a.topic });
    return acc;
  }, []);

  // filtered attendance list
  const filtered = session
    ? attendance.filter(a =>
        a.date===session.date &&
        a.topic===session.topic &&
        (statusFilter==='all'? true : a.status===statusFilter)
      )
    : [];

  return (
    <div className="p-6 space-y-6">
      <button onClick={onBack} className="text-blue-600 underline">
        &larr; Back to Batches
      </button>

      <h2 className="text-2xl font-bold">{batch.batch_name} Details</h2>
      <p className="mb-2">Teacher: {teacherName}</p>

      {/* Add Student */}
      {showAdd ? (
        <form onSubmit={handleAddSubmit} className="p-4 border rounded bg-gray-50 space-y-2">
          <h3 className="font-semibold">Add Student</h3>
          {[
            ['Name','name','text'],
            ['Email','email','email'],
            ['Mobile','mobile','text'],
            ['Class','class','text'],
            ['School','school','text'],
            ['Father Name','father_name','text'],
            ['Father Phone','father_phone','text'],
            ['Mother Name','mother_name','text'],
            ['Mother Phone','mother_phone','text'],
            ['Join Date','date_of_joining','date'],
            ['Total Fees','total_fees','number']
          ].map(([label, key, type]) => (
            <div key={key}>
              <label className="block text-sm font-medium">{label}</label>
              <input
                type={type}
                value={newStudent[key]}
                onChange={e => setNewStudent(ns => ({ ...ns, [key]: e.target.value }))}
                required
                className="w-full px-2 py-1 border rounded"
              />
            </div>
          ))}
          {addError && <p className="text-red-600">{addError}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={adding}
              className="px-3 py-1 bg-green-600 text-white rounded"
            >
              {adding ? 'Adding…' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="px-3 py-1 bg-gray-300 rounded"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1 bg-blue-600 text-white rounded"
        >
          Add Student
        </button>
      )}

      {/* View / Delete Batch */}
      <div className="flex items-center gap-4 mt-4">
        <label className="font-medium">View:</label>
        <select
          value={viewType}
          onChange={e => setViewType(e.target.value)}
          className="px-2 py-1 border rounded"
        >
          <option value="attendance">Attendance Records</option>
          {tests.map(t => (
            <option key={t.id} value={t.id}>{t.topic}</option>
          ))}
        </select>
        <button
          onClick={handleDeleteBatch}
          className="ml-auto px-3 py-1 bg-red-600 text-white rounded"
        >
          Delete Batch
        </button>
      </div>

      {/* Attendance Section */}
      {viewType === 'attendance' && (
        <div>
          <h3 className="text-xl font-semibold mb-2">Attendance Records</h3>
          {!session ? (
            attendance.length === 0 ? (
              <p>No attendance recorded.</p>
            ) : (
              <ul className="space-y-2">
                {sessions.map(s => (
                  <li key={s.key} className="flex justify-between border-b py-2">
                    <span><strong>{s.date}</strong> – {s.topic}</span>
                    <button
                      onClick={() => { setSession(s); setStatusFilter('all'); }}
                      className="px-2 py-1 bg-blue-600 text-white rounded"
                    >
                      View Details
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="mt-4 space-y-4">
              <p><strong>Session:</strong> {session.date} – {session.topic}</p>
              <div>
                <label className="mr-2">Status:</label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="px-2 py-1 border rounded"
                >
                  <option value="all">All</option>
                  <option value="present_online">Online</option>
                  <option value="present_offline">Offline</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              {filtered.length === 0 ? (
                <p>No records for this status.</p>
              ) : (
                <ul className="list-disc list-inside space-y-1">
                  {filtered.map((a, i) => (
                    <li key={i}>
                      {studentsMap[a.student_id] || 'Unknown'} — {a.status.replace('_',' ')}{a.status==='absent'&&a.reason?` (Reason: ${a.reason})`:''}
                    </li>
                  ))}
                </ul>
              )}
              <button onClick={() => setSession(null)} className="text-blue-600 underline">
                Back to Sessions
              </button>
            </div>
          )}
        </div>
      )}

      {/* Test Leaderboard */}
      {viewType !== 'attendance' && (
        <div>
          <h3 className="text-xl font-semibold mb-2">Leaderboard: {tests.find(t=>t.id===viewType)?.topic}</h3>
          {loadingTest ? (
            <p>Loading…</p>
          ) : !testResults.length ? (
            <p>No test results.</p>
          ) : (
            <ol className="list-decimal list-inside">
              {testResults.map(r => (
                <li key={r.student_id}>
                  {r.student_name || studentsMap[r.student_id] || 'Unknown'} — {r.marks}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
